import { CSSProperties, JSX, useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';

import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';

import styles from './checkbox.module.css';
import { isDefined } from '@/utils/is-defined';

interface CheckboxProps {
  checked: boolean;
  value: string | number;
  onChange?: (e: string | number, isOn: boolean, metadata?: Record<string, string | number>) => void;
  partial?: boolean;
  size?: number;
  selectedIconName?: string;
  unselectedIconName?: string;
  color?: string;
  transparent?: boolean;
  activeColor?: string;
  inactiveColor?: string;
  backgroundColor?: string;
  label?: JSX.Element | string;
  disabled?: boolean;
  disabledOpacity?: CSSProperties['opacity'];
  labelClassName?: string;
  metadata?: Record<string, string | number>;
  className?: string;
  inert?: boolean;
}

const Checkbox = (props: CheckboxProps) => {
  const {
    onChange,
    value,
    checked,
    partial,
    size = 20,
    color = '',
    inactiveColor = '',
    activeColor = '',
    transparent = false,
    selectedIconName,
    unselectedIconName,
    backgroundColor = 'transparent',
    label = '',
    disabled,
    disabledOpacity,
    labelClassName = '',
    metadata,
    className,
    inert,
  } = props;

  const [isFocused, setIsFocused] = useState(false);

  const memoizedChange = useCallback(
    (e?: React.MouseEvent<HTMLDivElement>) => {
      e?.stopPropagation();
      if (disabled) return;
      onChange?.(value, !checked, metadata);
    },
    [disabled, onChange, value, checked, metadata]
  );

  const iconColor = useMemo(() => {
    if (checked) {
      if (activeColor) {
        return activeColor;
      }
      if (color) {
        return color;
      }
      return 'var(--gray-100)';
    } else {
      if (inactiveColor) {
        return inactiveColor;
      }
      if (color) {
        return color;
      }
      return 'var(--gray-100)';
    }
  }, [checked, activeColor, inactiveColor, color]);

  const style = useMemo(() => {
    const style: CSSProperties = {
      ...(isDefined(disabledOpacity)
        ? {
            '--checkbox-disabled-opacity': disabledOpacity,
          }
        : {}),
    } as CSSProperties;
    if (size && !label) {
      style.height = size;
      style.width = size;
    }

    return style;
  }, [disabledOpacity, size, label]);

  const icon = useMemo(() => {
    if (partial) {
      return ICONS.CHECKBOX_CHECKED_PARTIAL;
    }

    if (checked) {
      if (selectedIconName) {
        return selectedIconName;
      } else {
        return ICONS.CHECKBOX_CHECKED;
      }
    } else {
      if (unselectedIconName) {
        return unselectedIconName;
      } else {
        return ICONS.CHECKBOX;
      }
    }
  }, [checked, partial, selectedIconName, unselectedIconName]);

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (disabled) return;
      // if spacebar, prevent scroll
      if (e.key === ' ' && isFocused) {
        e.preventDefault();
      }
      // if enter or spacebar, trigger click
      if ((e.key === 'Enter' || e.key === ' ') && isFocused) {
        memoizedChange();
      }
    };
    document.addEventListener('keypress', handleKeyPress);
    return () => {
      document.removeEventListener('keypress', handleKeyPress);
    };
  }, [isFocused, memoizedChange, disabled]);

  return (
    <div
      className={classNames(styles.checkbox, className || '', 'flex fd-row jc-flex-start ai-center', {
        transparent,
        'has-label': !!label,
        'checkbox-active': checked,
        [styles.disabled]: disabled,
        partial,
      })}
      style={style}
      onClick={memoizedChange}
      tabIndex={disabled ? -1 : 0}
      role="checkbox"
      onFocus={() => {
        if (!disabled) {
          setIsFocused(true);
        }
      }}
      onBlur={() => setIsFocused(false)}
      inert={inert}
    >
      <div className="flex" style={{ minWidth: size, minHeight: size }}>
        <Icon size={size} color={iconColor} name={icon} backgroundColor={backgroundColor} />
      </div>

      {label ? (
        typeof label === 'string' ? (
          <span className={`${styles.label} margin-l-4 ${labelClassName}`}>{label}</span>
        ) : (
          label
        )
      ) : null}
    </div>
  );
};

export default Checkbox;
